"use client";

import { useEffect, useState } from "react";
import {
  MediaBufferingIndicator,
  MediaFullscreenButton,
  MediaMuteButton,
  MediaOutlet,
  MediaPIPButton,
  MediaPlayer,
  MediaPlayButton,
  MediaPoster,
  MediaTime,
  MediaTimeSlider,
  MediaTooltip,
} from "@vidstack/react";
import {
  Maximize2,
  Minimize2,
  Pause,
  PictureInPicture2,
  Play,
  RefreshCw,
  RotateCcw,
  Volume1,
  Volume2,
  VolumeX,
} from "lucide-react";
import { resolveTherapistVideo } from "~/lib/therapist-video-assets";
import { cn } from "~/lib/utils";

interface VideoPlayerProps {
  src: string;
  title: string;
  autoPlay?: boolean;
  className?: string;
  aspectRatio?: number;
  poster?: string;
  posterAlt?: string;
  contextLabel?: string;
  onAspectRatioChange?: (ratio: number) => void;
  onSourceError?: () => void;
}

/**
 * The shared, accessible playback surface for on-demand video. Keep capture and
 * upload previews on the native element because they require direct media access.
 */
function ActiveVideoPlayer({
  src,
  title,
  autoPlay = false,
  className,
  aspectRatio,
  poster,
  posterAlt = "",
  contextLabel,
  onAspectRatioChange,
  onSourceError,
}: VideoPlayerProps) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [retryKey, setRetryKey] = useState(0);
  const videoSrc = { src, type: "video/mp4" };

  const retry = () => {
    setHasError(false);
    setRetryKey((currentKey) => currentKey + 1);
  };

  return (
    <MediaPlayer
      key={retryKey}
      aria-label={title}
      aspectRatio={aspectRatio}
      autoPlay={autoPlay}
      className={cn("resimo-video-player", className)}
      load="eager"
      onPlaying={() => setIsPlaying(true)}
      onWaiting={() => setIsPlaying(false)}
      onLoadedMetadata={(event: { currentTarget: HTMLElement }) => {
        const video = event.currentTarget.querySelector("video");
        if (video?.videoWidth && video.videoHeight)
          onAspectRatioChange?.(video.videoWidth / video.videoHeight);
      }}
      onError={() => {
        setIsPlaying(false);
        if (onSourceError) onSourceError();
        else setHasError(true);
      }}
      poster={poster}
      posterLoad="eager"
      src={videoSrc}
      streamType="on-demand"
      title={title}
    >
      <MediaOutlet />
      {poster && <MediaPoster alt={posterAlt} />}
      {poster && !isPlaying && (
        <img
          src={poster}
          alt=""
          className="pointer-events-none absolute inset-0 z-10 h-full w-full object-cover"
        />
      )}
      <MediaBufferingIndicator className="resimo-video-player__buffering" />
      <div aria-hidden="true" className="resimo-video-player__loading-copy">
        Učitavanje videa…
      </div>
      {contextLabel && (
        <div aria-hidden="true" className="resimo-video-player__context">
          {contextLabel}
        </div>
      )}
      {hasError && (
        <div className="resimo-video-player__error" role="status">
          <p>Video trenutno nije moguće učitati.</p>
          <button type="button" onClick={retry}>
            <RefreshCw aria-hidden="true" />
            Pokušaj ponovo
          </button>
        </div>
      )}
      <div className="resimo-video-player__controls">
        <MediaPlayButton className="resimo-video-player__center-play">
          <Play aria-hidden="true" slot="play" />
          <Pause aria-hidden="true" slot="pause" />
          <RotateCcw aria-hidden="true" slot="replay" />
        </MediaPlayButton>

        <div className="resimo-video-player__control-bar">
          <MediaTimeSlider
            className="resimo-video-player__timeline"
            trackClass="h-1.5 w-full overflow-hidden rounded-full bg-white/30"
            trackFillClass="h-full bg-brand-decorative"
            trackProgressClass="h-full bg-white/20"
            thumbContainerClass="absolute inset-y-0"
            thumbClass="h-3.5 w-3.5 rounded-full border-2 border-white bg-brand-decorative shadow-xs"
          />

          <div className="flex items-center gap-1 text-sm font-medium text-white">
            <MediaPlayButton className="resimo-video-player__icon-button">
              <Play aria-hidden="true" slot="play" />
              <Pause aria-hidden="true" slot="pause" />
              <RotateCcw aria-hidden="true" slot="replay" />
              <MediaTooltip>
                <span slot="play">Pusti</span>
                <span slot="pause">Pauziraj</span>
                <span slot="replay">Pusti ponovo</span>
              </MediaTooltip>
            </MediaPlayButton>
            <MediaMuteButton className="resimo-video-player__icon-button">
              <Volume2 aria-hidden="true" slot="volume-high" />
              <Volume1 aria-hidden="true" slot="volume-low" />
              <VolumeX aria-hidden="true" slot="volume-muted" />
              <MediaTooltip>
                <span slot="mute">Isključi zvuk</span>
                <span slot="unmute">Uključi zvuk</span>
              </MediaTooltip>
            </MediaMuteButton>
            <MediaTime type="current" />
            <span aria-hidden="true" className="text-white/60">
              /
            </span>
            <MediaTime type="duration" />
            <MediaPIPButton className="resimo-video-player__icon-button ml-auto">
              <PictureInPicture2 aria-hidden="true" slot="enter" />
              <Minimize2 aria-hidden="true" slot="exit" />
              <MediaTooltip>
                <span slot="enter">Slika u slici</span>
                <span slot="exit">Zatvori sliku u slici</span>
              </MediaTooltip>
            </MediaPIPButton>
            <MediaFullscreenButton className="resimo-video-player__icon-button">
              <Maximize2 aria-hidden="true" slot="enter" />
              <Minimize2 aria-hidden="true" slot="exit" />
              <MediaTooltip position="top right">
                <span slot="enter">Ceo ekran</span>
                <span slot="exit">Izađi iz celog ekrana</span>
              </MediaTooltip>
            </MediaFullscreenButton>
          </div>
        </div>
      </div>
    </MediaPlayer>
  );
}

function RequestedVideoPlayer(props: VideoPlayerProps) {
  const [requested, setRequested] = useState(props.autoPlay ?? false);
  const [source, setSource] = useState<string>();
  useEffect(() => {
    if (!requested) return;
    let cancelled = false;
    setSource(undefined);
    void resolveTherapistVideo(props.src, "full").then((url) => {
      if (!cancelled) setSource(url);
    });
    return () => {
      cancelled = true;
    };
  }, [props.src, requested]);
  if (!source) {
    return (
      <button
        type="button"
        aria-label={props.title}
        disabled={requested}
        onClick={() => setRequested(true)}
        className={cn(
          "relative flex min-h-48 w-full items-center justify-center overflow-hidden bg-black text-white",
          props.className,
        )}
        style={{ aspectRatio: props.aspectRatio }}
      >
        {props.poster && (
          <img
            src={props.poster}
            alt={props.posterAlt ?? ""}
            className="absolute inset-0 h-full w-full object-cover opacity-70"
          />
        )}
        <span className="relative">
          {requested ? "Učitavanje videa…" : <Play aria-hidden="true" />}
        </span>
      </button>
    );
  }
  return (
    <ActiveVideoPlayer
      {...props}
      src={source}
      autoPlay
      onSourceError={
        source !== props.src ? () => setSource(props.src) : undefined
      }
    />
  );
}

export function VideoPlayer(props: VideoPlayerProps) {
  return <RequestedVideoPlayer key={props.src} {...props} />;
}
